import React from "react";
import { toast as rtoast } from "react-toastify";

/**
 * GLOBAL toast shape.
 *
 * The app has two ways of raising a toast and they LOOKED different:
 *
 *   • 166 files go through `components/ui` toast, which is handed a
 *     `<Notification title="Successfully Done" type="success">` and renders a
 *     bold title line above the message.
 *   • 44 files (all of ChequeGuard, ~175 calls) import react-toastify directly
 *     and pass a bare string -- so those toasts came out with no title at all.
 *
 * Both land in the SAME <ToastContainer> in App.js, so this was never a
 * position or colour problem: ChequeGuard toasts were simply missing the title
 * row every other screen has.
 *
 * Rather than rewrite 175 call sites -- which would also mean every new
 * ChequeGuard screen has to remember the convention -- this wraps
 * react-toastify's own methods once. A bare string gets the standard title for
 * its type; anything already built as an element is passed through untouched,
 * so `components/ui` toasts are unaffected and nothing is double-wrapped.
 *
 * Add a screen-specific title by passing the element form
 * (`toast.push(<Notification title="Deleted" .../>)`), exactly as the rest of
 * the app does. Do NOT add a local toast helper -- see ApiService for the
 * project's one place that decides colour and title for API results.
 */

const DEFAULT_TITLES = {
    success: "Successfully Done",
    error: "Error",
    warning: "Warning",
    info: "Information",
};

const titled = (title, message) => (
    <div>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{title}</div>
        <div>{message}</div>
    </div>
);

/** Only a bare string (or number) needs a title added. */
const needsTitle = (content) =>
    typeof content === "string" || typeof content === "number";

let installed = false;

export const installToastTitles = () => {
    if (installed) return;
    installed = true;

    // `warn` is react-toastify's alias for `warning`; ChequeGuard uses both.
    const methods = ["success", "error", "warning", "warn", "info"];

    methods.forEach((method) => {
        const original = rtoast[method];
        if (typeof original !== "function") return;

        const title = DEFAULT_TITLES[method === "warn" ? "warning" : method];

        const wrapped = (content, ...rest) =>
            original(needsTitle(content) ? titled(title, content) : content, ...rest);

        // react-toastify hangs helpers off these functions; keep them reachable.
        Object.assign(wrapped, original);
        rtoast[method] = wrapped;
    });
};

export default installToastTitles;
